import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Crumbs, ErrorState, Spinner, StatusBadge } from "@/components/common";
import { ImageUploader } from "@/components/ImageUploader";
import { api, apiError, TYPE_SINGULAR } from "@/lib/api";
import { CostLines, CURRENCIES, LiveSummary, MoneyInput, Stepper } from "@/components/experience/BudgetCalculator";
import { CityCombobox } from "@/components/apply/CityCombobox";
import { DateRangeField } from "@/components/experience/DateRangeField";
import { VideoUploader } from "@/components/experience/VideoUploader";
import { StaysEditor } from "@/components/experience/StaysEditor";
import { CoverPicker, coverCandidates } from "@/components/experience/CoverPicker";
import { useAuth } from "@/context/AuthContext";
import { typeContent } from "@/lib/experienceTypes";

const EMPTY = { title: "", type: "villa", destination: "", venue: "", bedrooms: "", dates_flexible: true, start_date: "", end_date: "", summary: "", description: "", highlights: ["", "", ""],
  images: [], cover: null, captions: {}, video: null, stays: [], guest_places: 4, cohost_slots: 4, split_mode: "equal", currency: "EUR", cost_items: [], budget_total: "" };
const itemsTotal = (items) => items.reduce((t, it) => t + (Number(it.amount) || 0), 0);

const Field = ({ id, label, hint, error, children }) => (
  <div>
    <label htmlFor={id} className="field-label">{label} {hint && <span className="font-normal text-muted-foreground">{hint}</span>}</label>
    {children}
    {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
  </div>
);

const StatusControls = ({ exp, onDone }) => {
  const change = async (status, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await api.post(`/experiences/${exp.id}/status`, { status }); toast.success("Status updated"); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="surface space-y-4 p-6" data-testid="status-controls">
      <div className="flex items-center justify-between"><h2 className="font-display text-2xl">Status</h2><StatusBadge status={exp.status} /></div>
      <div className="flex flex-wrap gap-3">
        {exp.status === "finding_cohosts" && exp.cohost_count === 1 && exp.guest_count === 0 && <button type="button" className="btn-outline min-h-[44px]" onClick={() => change("draft")} data-testid="status-to-draft">Move back to draft</button>}
        {exp.status === "agreed" && <button type="button" className="btn-outline min-h-[44px]" onClick={() => change("closed", "Close this experience? The circle will no longer accept changes.")} data-testid="status-to-closed">Close experience</button>}
        {!["closed", "cancelled"].includes(exp.status) && <button type="button" className="btn-outline min-h-[44px] border-destructive/60 text-destructive hover:border-destructive hover:text-destructive" onClick={() => change("cancelled", "Cancel this experience for everyone in the circle?")} data-testid="status-to-cancelled">Cancel experience</button>}
      </div>
    </section>
  );
};

export default function ExperienceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(id ? null : EMPTY);
  const [exp, setExp] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const load = async () => {
    try {
      const { data } = await api.get(`/experiences/${id}`);
      if (data.my_role !== "lead" && user?.role !== "admin") return navigate(`/experiences/${id}`, { replace: true });
      setExp(data);
      setF({ ...EMPTY, ...data, start_date: data.start_date || "", end_date: data.end_date || "", highlights: [...data.highlights, "", "", ""].slice(0, 3),
        video: data.video || null, cover: data.cover || null, captions: data.captions || {}, bedrooms: data.bedrooms ?? "", budget_total: data.budget_total ?? "", cost_items: data.cost_items || [], stays: data.stays || [] });
    } catch (e) { setLoadError(apiError(e)); }
  };
  useEffect(() => { if (id) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const e = {};
    if (f.title.trim().length < 3) e.title = "Give the experience a title (3+ characters)";
    if (f.destination.trim().length < 2) e.destination = "Add a destination";
    if (f.summary.trim().length < 10) e.summary = "Write a short summary (10+ characters)";
    if (!f.dates_flexible && !f.start_date) e.dates = "Add a start date or mark dates as flexible";
    if (f.start_date && f.end_date && f.end_date < f.start_date) e.dates = "End date must be after the start date";
    if (f.cost_items.some((it) => !it.label.trim() || it.amount === "" || Number(it.amount) < 0)) e.costs = "Give every cost line a name and an amount";
    if (f.stays.some((s) => !s.title.trim())) e.stays = `Give every ${typeContent(f.type).listing.item.toLowerCase()} a name`;
    if (exp && f.cohost_slots < exp.cohost_count) e.slots = `You already have ${exp.cohost_count} co-hosts`;
    setErrors(e);
    return !Object.keys(e).length;
  };

  const save = async (publish) => {
    if (!validate()) return toast.error("Please check the highlighted fields");
    setBusy(publish ? "publish" : "save");
    const lead = f.stays.find((s) => s.title.trim()) || f.stays[0];
    const roomSpec = lead?.specs?.bedrooms ?? lead?.specs?.cabins;
    const allPhotos = coverCandidates(f).map((o) => o.path);
    const body = { ...f, publish, start_date: f.start_date || null, end_date: f.end_date || null, highlights: f.highlights.filter((h) => h.trim()),
      guest_places: Number(f.guest_places) || 0,
      cover: allPhotos.includes(f.cover) ? f.cover : null,
      captions: Object.fromEntries(Object.entries(f.captions || {}).filter(([p, t]) => allPhotos.includes(p) && t.trim()).map(([p, t]) => [p, t.trim()])),
      venue: lead ? lead.title.trim().slice(0, 120) : f.venue,
      bedrooms: roomSpec && Number(roomSpec) >= 0 ? Math.min(60, Math.floor(Number(roomSpec))) : f.bedrooms === "" ? null : Number(f.bedrooms),
      cost_items: f.cost_items.map((it) => ({ label: it.label.trim(), amount: Number(it.amount) })),
      stays: f.stays.map((s) => ({ ...s, title: s.title.trim(), description: s.description.trim(),
        specs: Object.fromEntries([...tc.specs.map(([k]) => k), ...(tc.extra || [])].map((k) => [k, (s.specs?.[k] || "").trim()]).filter(([, v]) => v)),
        route: tc.route ? (s.route || []).filter((pt) => pt.label.trim()).map((pt) => ({ label: pt.label.trim(), ll: pt.ll || "", date: pt.date || "" })) : [] })), budget_total: f.budget_total === "" ? null : Number(f.budget_total) };
    try {
      const { data } = id ? await api.put(`/experiences/${id}`, body) : await api.post("/experiences", body);
      toast.success(id ? "Changes saved" : publish ? "Experience published" : "Draft saved");
      navigate(`/experiences/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (loadError) return <div className="shell py-16"><ErrorState message={loadError} /></div>;
  if (!f) return <Spinner />;
  const isDraft = !id || exp?.status === "draft";
  const tc = typeContent(f.type);
  const setItems = (items) => setF((p) => ({ ...p, cost_items: items, budget_total: items.length ? itemsTotal(items) : p.budget_total }));

  return (
    <div className="shell max-w-[1200px] pb-24 pt-8" data-testid="experience-form-page">
      <Crumbs items={[{ label: "My circle", to: "/circle" }, { label: id ? "Edit experience" : "Create experience" }]} />
      <p className="eyebrow mb-3 mt-6">Private planning space</p>
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">{id ? "Edit the experience." : "Propose an experience."}</h1>
      <p className="mt-4 max-w-xl text-foreground/85">Set the scene, build the budget and decide how many co-hosts share it. Each co-host confirms their share in the planning space.</p>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_340px]">
      <form className="min-w-0 space-y-8" onSubmit={(e) => { e.preventDefault(); save(isDraft ? false : false); }} noValidate>
        <section className="surface space-y-6 p-6 sm:p-8">
          <Field id="x-title" label="Title" error={errors.title}><input id="x-title" className="field" maxLength={90} value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder={tc.title} data-testid="exp-title-input" /></Field>
          <fieldset>
            <legend className="field-label">Experience type</legend>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_SINGULAR).map(([k, v]) => <button key={k} type="button" className="chip" data-active={f.type === k} aria-pressed={f.type === k} onClick={() => set("type")(k)} data-testid={`exp-type-${k}`}>{v}</button>)}
            </div>
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <CityCombobox id="x-dest" label="Destination" placeholder="Search a city, e.g. Ibiza" value={f.destination} onChange={set("destination")} error={errors.destination} testId="exp-destination-input" />
            <div className="space-y-3 sm:pt-7">
              <label className="flex min-h-[44px] items-center justify-between gap-4 rounded-[10px] border border-border px-4">
                <span>Dates are flexible</span>
                <Switch checked={f.dates_flexible} onCheckedChange={set("dates_flexible")} data-testid="exp-flexible-switch" />
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <DateRangeField start={f.start_date} end={f.end_date} optional={f.dates_flexible} onChange={(a, b) => setF((p) => ({ ...p, start_date: a, end_date: b }))} />
            {errors.dates && <p className="text-sm text-destructive" role="alert">{errors.dates}</p>}
          </div>
        </section>
        <section className="surface space-y-6 p-6 sm:p-8">
          <Field id="x-summary" label="The idea in a sentence" error={errors.summary}><textarea id="x-summary" rows={2} maxLength={220} className="field py-3" value={f.summary} onChange={(e) => set("summary")(e.target.value)} placeholder="An exceptional weekend. A shared plan." data-testid="exp-summary-input" /></Field>
          <Field id="x-desc" label="Description" hint="(optional)"><textarea id="x-desc" rows={4} maxLength={3000} className="field py-3" value={f.description} onChange={(e) => set("description")(e.target.value)} data-testid="exp-description-input" /></Field>
          <fieldset>
            <legend className="field-label">Highlights <span className="font-normal text-muted-foreground">(up to 3)</span></legend>
            <div className="grid gap-3 sm:grid-cols-3">
              {f.highlights.map((h, i) => <input key={i} className="field" maxLength={80} value={h} aria-label={`Highlight ${i + 1}`} placeholder={tc.highlights[i]}
                onChange={(e) => set("highlights")(f.highlights.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`exp-highlight-${i}`} />)}
            </div>
          </fieldset>
        </section>
        <StaysEditor value={f.stays} type={f.type} onChange={set("stays")} captions={f.captions} onCaptions={set("captions")} />
        {errors.stays && <p className="-mt-4 text-sm text-destructive" role="alert" data-testid="stays-error">{errors.stays}</p>}
        <section className="surface space-y-6 p-6 sm:p-8" data-testid="media-section">
          <div><p className="eyebrow">Cover & video</p><h2 className="mt-2 font-display text-3xl">Set the scene</h2>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="cover-hint">{f.stays.some((s) => s.images?.length) ? `The first ${tc.listing.item.toLowerCase()} photo is the cover unless you pick another below.` : "Add a cover photo, or skip this and the first listing photo is used."}</p></div>
          <CoverPicker f={f} onChange={set("cover")} />
          <div>
            <p className="field-label">Own photos <span className="font-normal text-muted-foreground">(optional · JPG/PNG/WEBP up to 8MB)</span></p>
            <ImageUploader value={f.images} onChange={set("images")} testId="exp-images" coverBadge={false} />
          </div>
          <VideoUploader value={f.video} onChange={set("video")} onBusy={setVideoBusy} />
        </section>
        <section className="surface space-y-6 p-6 sm:p-8" data-testid="budget-section">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><p className="eyebrow">Budget calculator</p><h2 className="mt-2 font-display text-3xl">What will it cost?</h2></div>
            <select className="field w-[110px]" value={f.currency} onChange={(e) => set("currency")(e.target.value)} aria-label="Currency" data-testid="exp-currency-select">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <CostLines type={f.type} items={f.cost_items} currency={f.currency} onChange={setItems} />
          {errors.costs && <p className="text-sm text-destructive" role="alert">{errors.costs}</p>}
          <Field id="x-total" label="Total budget" hint={f.cost_items.length ? "(adds up your cost lines)" : "(or enter a total)"}>
            <MoneyInput id="x-total" className="max-w-[260px]" currency={f.currency} value={f.budget_total} readOnly={!!f.cost_items.length}
              onChange={set("budget_total")} data-testid="exp-budget-input" />
          </Field>
        </section>
        <section className="surface space-y-6 p-6 sm:p-8" data-testid="places-section">
          <div><p className="eyebrow">The circle</p><h2 className="mt-2 font-display text-3xl">Who shares it, who's invited</h2></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Stepper label="Co-host places" hint="Including you. They split the total." value={Number(f.cohost_slots)} min={Math.max(1, exp?.cohost_count || 1)} max={30} onChange={set("cohost_slots")} testId="exp-cohost-slots" />
            <Stepper label="Guest places" hint="Invited guests. Their costs are set in the guest terms." value={Number(f.guest_places)} min={exp?.guest_count || 0} max={50} onChange={set("guest_places")} testId="exp-guest-places" />
          </div>
          {errors.slots && <p className="text-sm text-destructive" role="alert">{errors.slots}</p>}
          <fieldset>
            <legend className="field-label">How co-hosts split the total</legend>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" data-active={f.split_mode === "equal"} aria-pressed={f.split_mode === "equal"} onClick={() => set("split_mode")("equal")} data-testid="exp-split-equal">Equal shares</button>
              <button type="button" className="chip" data-active={f.split_mode === "custom"} aria-pressed={f.split_mode === "custom"} onClick={() => set("split_mode")("custom")} data-testid="exp-split-custom">Custom shares</button>
            </div>
          </fieldset>
        </section>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn-outline" onClick={() => navigate(-1)} data-testid="exp-cancel-button">Cancel</button>
          <button type="button" className="btn-outline" onClick={() => save(false)} disabled={!!busy || videoBusy} data-testid="exp-save-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : isDraft ? "Save draft" : "Save changes"}</button>
          {isDraft && <button type="button" className="btn-primary" onClick={() => save(true)} disabled={!!busy || videoBusy} data-testid="exp-publish-button">{busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish & find co-hosts"}</button>}
        </div>
      </form>
      <LiveSummary f={f} />
      </div>
      {exp && <div className="mt-10"><StatusControls exp={exp} onDone={load} /></div>}
    </div>
  );
}
